/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@we-make-websites/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { isCdnUrl } from '~/helpers/general'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product

  const formattedProduct = {
    available: product.available,
    collectionImage: product.collectionImage?.reference?.image,
    compareAtPrice: formatProductPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    media: formatMedia(product),
    options: formatOptions(product),
    price: formatProductPrice(product.priceRange),
    secondaryImage: product.secondaryImage?.reference?.image,
    relatedColoursCount: product.relatedCollection?.reference?.products?.edges?.length - 1,
    relatedCollection: transformEdges(product.relatedCollection?.reference?.products),
    tags: product.tags,
    title: product.title,
    subtitle: product.subtitle?.value,
    type: product.type,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    variants: formatVariants(product),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))
  formattedProduct.featuredImage = featuredImages[0]

  if (!formattedProduct.secondaryImage) {
    formattedProduct.secondaryImage = featuredImages.length > 1
      ? featuredImages[1]
      : {}
  }

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats product price.
 * @param {Object} price - Product price response.
 * @returns {Object}
 */
function formatProductPrice(price) {
  return {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @param {Array} [images] - Custom array of images for formatting.
 * @returns {Array}
 */
function formatImages(product, images = false) {
  const imagesArray = images ? images : transformEdges(product?.images)

  if (!imagesArray.length) {
    return []
  }

  const formattedImages = imagesArray.map((image, index) => {
    if (!image.url) {
      return {}
    }

    const linkedVariant = product.variants?.length
      ? transformEdges(product.variants).find((variant) => {
        return variant.image.id === image.id
      })
      : false

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      index,
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })

  if (images.length === 1) {
    return formattedImages[0]
  }

  return formattedImages
}

/**
 * Formats media.
 * @param {Oject} product - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  const mediaArray = transformEdges(product.media)

  if (!mediaArray.length) {
    return []
  }

  return mediaArray.map((media, index) => {
    const formattedMedia = {
      alt: media.alt,
      id: decodeApiId(media.id) || false,
      index,
      mediaType: media.mediaType.toLowerCase(),
    }

    switch (formattedMedia.mediaType) {
      case 'external_video':
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.host = media.host
        formattedMedia.previewImage = formatImages(false, [media.previewImage])
        break

      case 'image':
        formattedMedia.image = formatImages(false, [media.image])
        break

      case 'model_3d':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            mimeType: source.mimeType,
            url: source.url,
          }
        })
        break

      case 'video':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            height: source.height,
            mimeType: source.mimeType,
            url: source.url,
            width: source.width,
          }
        })
        break
    }

    return formattedMedia
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatVariants(product) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: formatVariantPrice(variant.compareAtPrice),
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) ?? false,
      inventory: variant.quantityAvailable,
      price: formatVariantPrice(variant.price),
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift card variants show price as their title for localisation purposes.
     */
    formattedVariant.title = product.giftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats variant price.
 * @param {Object} price - Variant price response.
 * @returns {Object}
 */
function formatVariantPrice(price) {
  if (!price) {
    return {
      amount: false,
      currencyCode: false,
    }
  }

  return {
    amount: Number(price.amount) * 100,
    currencyCode: price.currencyCode,
  }
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
      totalCount: 0,
    },
    filters: Object.values(collection.products?.filters),
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format filters.
   */
  formattedCollection.filters?.forEach(
    filter => filter.values?.forEach(
      value => {
        value.input = JSON.parse(value.input)
      },
    ),
  )

  /**
   * Get total product count using vendors filter.
   */
  const vendorFilter = formattedCollection.filters?.find(
    filter => filter.id === 'filter.p.vendor',
  )

  if (vendorFilter) {
    const totalProducts = vendorFilter.values?.reduce((a, b) => {
      return a.count + b.count
    })

    if (vendorFilter.values?.length <= 1) {
      formattedCollection.pageInfo.totalCount = totalProducts?.count
    } else {
      formattedCollection.pageInfo.totalCount = totalProducts
    }
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return {
      available: product.available,
      collectionImage: product.collectionImage?.reference?.image,
      compareAtPrice: formatProductPrice(product.compareAtPriceRange),
      featuredImage: formatImages(product)[0],
      handle: product.handle,
      id: decodeApiId(product.id),
      media: formatImages(product)[0],
      price: formatProductPrice(product.priceRange),
      relatedColoursCount: product.relatedCollection?.reference?.products?.edges?.length - 1,
      relatedCollection: transformEdges(product.relatedCollection?.reference?.products),
      variants: formatVariants(product),
      secondaryImage: product.secondaryImage?.reference?.image,
      tags: product.tags,
      title: product.title,
      type: product.type,
      url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    }
  })

  return formattedCollection
}

/**
 * Formats response from predictive search results.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatPredictiveSearchResults(response) {
  const results = response.predictiveSearch

  const formattedResults = {
    pages: results?.pages,
    queries: results?.queries,
  }

  formattedResults.products = results.products
    .map((item) => ({
      available: item.available,
      compareAtPrice: formatProductPrice(item.compareAtPriceRange),
      media: formatMedia(item),
      featuredImage: item.featuredImages.edges[0].node,
      handle: item.handle,
      price: formatProductPrice(item.priceRange),
      relatedColoursCount: item.relatedCollection?.reference?.products?.edges?.length - 1,
      tags: item.tags,
      title: item.title,
      type: item.type,
      url: formatProductUrl(
        `//${location.host}${cnvs.routes.normalisedRoot}products/${item.handle}`,
        null,
      ),
    }))

  return formattedResults
}

/**
 * Formats response from search results.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatSearchResults(response) {
  const results = response.search

  const formattedResults = {
    totalCount: results?.totalCount,
    pageInfo: results?.pageInfo,
  }

  /**
   * Format products.
   */
  formattedResults.products = results.edges
    .filter((edge) => edge.node.resultType === 'Product')
    .map((edge) => ({
      ...edge.node,
      compareAtPrice: formatProductPrice(edge.node.compareAtPriceRange),
      featuredImage: formatImages(edge.node)[0],
      price: formatProductPrice(edge.node.priceRange),
      url: formatProductUrl(
        `//${location.host}${cnvs.routes.normalisedRoot}products/${edge.node.handle}`,
        edge.node.trackingParameters,
      ),
    }))

  /**
   * Format pages.
   */
  formattedResults.pages = results.edges
    .filter((edge) => edge.node.resultType === 'Page')
    .map((edge) => edge.node)

  /**
   * Format articles.
   */
  formattedResults.articles = results.edges
    .filter((edge) => edge.node.resultType === 'Article')
    .map((edge) => ({
      ...edge.node,
      content: edge.node.content.slice(0, 150) + '...'
    }))

  /**
   * Format filters.
   */
  formattedResults.productFilters = results.productFilters

  return formattedResults
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    /**
     * Storefront API URL always uses .myshopify.com domain.
     */
    const urlPath =
      object.url.split(cnvs.store.localisedShopDomain)[1] ||
      object.url.split(cnvs.store.localisedPermanentDomain)[1]

    const isHomepage =
      object.url.replace('https:', '') === cnvs.store.localisedShopDomain ||
      object.url.replace('https:', '') === cnvs.store.localisedPermanentDomain

    const url = urlPath || isHomepage
      ? `${cnvs.routes.normalisedRoot}${urlPath ? urlPath : ''}`
      : object.url

    return {
      handle,
      links,
      tier,
      title: object.title,
      url,
    }
  })
}

/**
 * Format Shopify image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  if (!isCdnUrl(src)) {
    return src
  }

  const filetype = src.split('.').reverse()[0]?.split('?')[0]
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    return src
  }

  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(/(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g, '.')

  url = `//${url}${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Format product URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2023-10/objects/Product#field-product-trackingparameters
 * @param {String} url - The URL of the object.
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatProductUrl(url, trackingParameters) {
  if (!trackingParameters) {
    return url
  }

  /**
   * Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${url.split('//')[1]}`
  const urlObject = new URL(localUrl)
  const trackingSearchParams = new URLSearchParams(trackingParameters)

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  })

  if (!parameters.size) {
    return url
  }

  const href = urlObject.href
    .split('?')[0]
    .replace('https:', '')

  return `${href}?${parameters.toString()}`
}
